import { useCallback, useEffect, useMemo, useRef } from 'react';

import { type RovoMemoriesResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

interface MemoryItem {
	id: string;
	content: string;
}

export interface UseMemoriesProps extends AssistanceServiceInjectedParams {
	/**
	 * Whether to enable fetching
	 */
	isEnabled: boolean;
}

export interface UseMemoriesReturn {
	/**
	 * The list of memories formatted for the UI
	 */
	memories: MemoryItem[];
	/**
	 * Whether memories are currently loading
	 */
	isLoading: boolean;
	/**
	 * Function to retry fetching memories
	 */
	onRetry: () => Promise<void>;
	/**
	 * Function to delete a memory by ID
	 */
	onDeleteMemory: (memoryId: string) => Promise<void>;
	/**
	 * Whether there was an error fetching memories
	 */
	error?: ApiError | null;
}

/**
 * Filters out invalid memories from the API response.
 *
 * A memory is considered invalid if:
 * - content is null, undefined, empty string, or whitespace-only
 * - content is the literal string "null" (case-insensitive)
 *
 * @param memories - Array of memory objects from the API
 * @returns Filtered array containing only valid memories
 * @internal Exported for testing purposes
 */
export function filterValidMemories(memories: RovoMemoriesResponse): RovoMemoriesResponse {
	return memories.filter((memory) => {
		const trimmedContent = memory.content?.trim();
		return trimmedContent && trimmedContent.toLowerCase() !== 'null';
	});
}

/**
 * Hook for fetching and managing agent memories
 * Handles memory fetching, deletion, and error states
 */
export const useMemories: (
	props: Omit<UseMemoriesProps, 'assistanceServiceInjectedParams'> & AssistanceServiceInputParam,
) => UseMemoriesReturn = createHookWithAssistanceService(
	({ assistanceServiceInjectedParams, isEnabled }: UseMemoriesProps): UseMemoriesReturn => {
		const { assistanceService } = assistanceServiceInjectedParams;

		// Fetch memories
		const {
			data: memoriesResponse,
			isFetching,
			refetch,
			error,
		} = useQuery<RovoMemoriesResponse>({
			queryKey: ['rovo-memories'],
			enabled: isEnabled,
			queryFn: assistanceService.getRovoMemories,
		});

		// Refetch memories when the modal is reopened (isEnabled transitions false → true)
		// Skip the initial mount since useQuery handles the first fetch
		const hasInitialized = useRef(false);
		useEffect(() => {
			if (!hasInitialized.current) {
				hasInitialized.current = true;
				return;
			}
			if (isEnabled) {
				refetch();
			}
		}, [isEnabled, refetch]);

		// Transform memories to MemoryItem format and filter out invalid memories
		const memories: MemoryItem[] = useMemo(() => {
			if (!memoriesResponse) {
				return [];
			}

			return filterValidMemories(memoriesResponse).map((memory) => ({
				id: memory.id,
				content: memory.content,
			}));
		}, [memoriesResponse]);

		const handleRetry = useCallback(async () => {
			await refetch();
		}, [refetch]);

		// Mutation for deleting a memory
		const { mutate: deleteMemory } = useMutation<string, void>({
			mutationFn: async (memoryId: string) => {
				return assistanceService.deleteRovoMemory(memoryId);
			},
			onSuccess: async () => {
				// Refetch memories after deletion to update the list
				await refetch();
			},
		});

		return {
			memories,
			isLoading: isFetching,
			onRetry: handleRetry,
			onDeleteMemory: deleteMemory,
			error,
		};
	},
);
